/**
 * The fields MSAL puts on a rejected sign-in.
 *
 * Neither `@azure/msal-browser` nor the popup and redirect flows declare a type
 * for what they reject with, so it has to be asserted wherever it is read.
 */
interface MsalErrorShape {
  /** MSAL's machine-readable code, e.g. `user_cancelled`. */
  errorCode?: string;
  /** MSAL's own description of the failure. */
  errorMessage?: string;
  /** Present when the rejection is an ordinary `Error` rather than an MSAL one. */
  message?: string;
  name?: string;
}

/**
 * The MSAL error fields, read safely.
 *
 * A type assertion does nothing at runtime, so asserting a rejected value and
 * then reading a property off it throws when the rejection is `null` or
 * `undefined` — inside the handler reporting the failure, which swallows the
 * original cause and replaces it with a `TypeError`. Returning an empty object
 * for a rejection with no value makes every field simply absent instead.
 *
 * Anything that is not an object gets the same treatment, so the returned value
 * is always the object shape it claims to be. A rejected string has none of
 * these fields either way, but returning it would hand the caller a primitive
 * that spreads and enumerates as one.
 */
export function msalError(error: unknown): MsalErrorShape {
  if (error === null || typeof error !== "object") return {};
  return error as MsalErrorShape;
}
